/**
 * FlightCalendar.jsx - Googleカレンダー風UI
 * 機体予約スケジュール管理
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { flightApi, droneApi, userApi, maintenanceApi } from '../services/droneOpsApi';
import { naturalSortBy } from '../services/utils';
import { Icon } from '../components/icons';
import { Button, Badge, Alert, Input, Select, Modal, Drawer, Tabs } from '../components/ui';
import { Textarea } from '../components/ui/Input';

const STATUS_LABELS = {
  planned:   '予約中',
  in_flight: '飛行中',
  completed: '完了',
  cancelled: 'キャンセル',
};

const STATUS_COLORS = {
  planned:   null,
  in_flight: '#10B981',
  completed: '#6B7280',
  cancelled: '#EF4444',
};

const OPERATIONAL_COLORS = {
  available:   '#22c55e',
  in_flight:   '#3b82f6',
  maintenance: '#f59e0b',
  repair:      '#ef4444',
  unknown:     '#64748b',
};

const OPERATIONAL_LABELS = {
  available:   '待機中',
  in_flight:   '飛行中',
  maintenance: '点検中',
  repair:      '修理中',
  unknown:     '不明',
};

const MAINT_TYPE_LABELS = {
  periodic_3m: '3ヶ月定期点検',
  periodic_1m: '1ヶ月定期点検',
  battery:     'バッテリー',
  repair:      '修理',
  pre_flight:  '飛行前点検',
  post_flight: '飛行後点検',
};

// 予約種別（カレンダー色分け用）- はっきりした色
const FLIGHT_CATEGORIES = {
  flight:      { label: '飛行',     color: '#0066FF' },  // 青
  inspection:  { label: '点検',     color: '#FF6600' },  // オレンジ
  training:    { label: '訓練',     color: '#9900FF' },  // 紫
  demo:        { label: 'デモ',     color: '#00CCFF' },  // 水色
  survey:      { label: '測量',     color: '#00AA00' },  // 緑
  spray:       { label: '散布',     color: '#CCCC00' },  // 黄
  other:       { label: 'その他',   color: '#666666' },  // グレー
};

const EMPTY_FORM = {
  thingName:  '',
  groupName:  '',
  pilotId:    '',
  pilotName:  '',
  category:   'flight',
  title:      '',
  startTime:  '',
  endTime:    '',
  location:   '',
  purpose:    '',
  notes:      '',
  altitude:   '',
};

// ミニカレンダーコンポーネント
function MiniCalendar({ selectedDate, onDateClick, onMonthChange }) {
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const days = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const today = new Date();
  const isToday = (day) =>
    day && year === today.getFullYear() && month === today.getMonth() && day === today.getDate();

  const isSelected = (day) =>
    day && selectedDate && year === selectedDate.getFullYear() && month === selectedDate.getMonth() && day === selectedDate.getDate();

  const prevMonth = () => {
    const newDate = new Date(year, month - 1, 1);
    setViewDate(newDate);
    onMonthChange?.(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(year, month + 1, 1);
    setViewDate(newDate);
    onMonthChange?.(newDate);
  };

  const handleDayClick = (day) => {
    if (day) {
      onDateClick?.(new Date(year, month, day));
    }
  };

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
          <Icon name="chevronLeft" className="w-4 h-4 text-text-secondary" />
        </button>
        <span className="text-sm font-medium text-text-primary">
          {year}年{month + 1}月
        </span>
        <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
          <Icon name="chevronRight" className="w-4 h-4 text-text-secondary" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0 text-center">
        {['日', '月', '火', '水', '木', '金', '土'].map((d, i) => (
          <div key={d} className={`text-xs py-1 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-outline'}`}>
            {d}
          </div>
        ))}
        {days.map((day, i) => (
          <button
            key={i}
            onClick={() => handleDayClick(day)}
            disabled={!day}
            className={`
              text-xs py-1 rounded-full w-7 h-7 mx-auto flex items-center justify-center
              ${!day ? '' : 'hover:bg-gray-100 cursor-pointer'}
              ${isToday(day) ? 'bg-nexus-primary text-white hover:bg-nexus-blue' : ''}
              ${isSelected(day) && !isToday(day) ? 'bg-nexus-blue-soft text-nexus-primary' : ''}
              ${i % 7 === 0 && day ? 'text-red-500' : ''}
              ${i % 7 === 6 && day ? 'text-blue-500' : ''}
            `}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FlightCalendar() {
  const calendarRef = useRef(null);

  const [flights,  setFlights]  = useState([]);
  const [drones,   setDrones]   = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [users,    setUsers]    = useState([]);
  const [alerts,   setAlerts]   = useState([]);

  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [formError,      setFormError]      = useState('');
  const [submitting,     setSubmitting]     = useState(false);
  const [currentRange,   setCurrentRange]   = useState(null);
  const [groupConfirm,   setGroupConfirm]   = useState(null);
  const [selectedDate,   setSelectedDate]   = useState(new Date());

  const [filterGroup, setFilterGroup] = useState('');
  const [filterThing, setFilterThing] = useState('');

  // 折りたたみ状態
  const [dronesExpanded, setDronesExpanded] = useState(true);
  const [groupsExpanded, setGroupsExpanded] = useState(true);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  // 機体の表示/非表示状態（Googleカレンダーのチェックボックス風）
  const [visibleThings, setVisibleThings] = useState(new Set());
  const [visibleGroups, setVisibleGroups] = useState(new Set());

  // セクション順序管理（ドラッグ&ドロップ対応）
  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = localStorage.getItem('fc-section-order');
    return saved ? JSON.parse(saved) : ['drones', 'groups'];
  });
  const [draggedSection, setDraggedSection] = useState(null);

  const handleSectionDragStart = (e, sectionId) => {
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sectionId);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleSectionDragEnd = (e) => {
    setDraggedSection(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleSectionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, targetSectionId) => {
    e.preventDefault();
    if (!draggedSection || draggedSection === targetSectionId) return;

    const newOrder = [...sectionOrder];
    const draggedIdx = newOrder.indexOf(draggedSection);
    const targetIdx = newOrder.indexOf(targetSectionId);

    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedSection);

    setSectionOrder(newOrder);
    localStorage.setItem('fc-section-order', JSON.stringify(newOrder));
  };

  // 初期化時に全機体を表示
  useEffect(() => {
    if (drones.length > 0 && visibleThings.size === 0) {
      setVisibleThings(new Set(drones.map(d => d.thingName)));
    }
  }, [drones]);

  useEffect(() => {
    if (groups.length > 0 && visibleGroups.size === 0) {
      setVisibleGroups(new Set(groups.map(g => g.groupName)));
    }
  }, [groups]);

  const filterGroupObj  = groups.find(g => g.groupName === filterGroup);
  const filteredDrones  = drones;

  const fetchFlights = useCallback(async (from, to) => {
    try {
      const data = await flightApi.list({ from, to });
      setFlights(data.flights ?? []);
    } catch (e) {
      console.error('予約取得エラー:', e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [droneData, userData, alertData] = await Promise.all([
          droneApi.list(),
          userApi.list(),
          maintenanceApi.alerts({ days: 7 }),
        ]);
        const droneList = droneData.drones ?? [];
        setDrones(naturalSortBy(droneList, 'thingName'));
        setUsers(naturalSortBy(userData.users ?? [], 'name'));
        setAlerts(alertData.alerts ?? []);

        try {
          const groupData = await droneApi.listGroups();
          setGroups(naturalSortBy(groupData.groups ?? [], 'displayName'));
        } catch {
          const grpSet = new Set();
          droneList.forEach(d => (d.groups ?? []).forEach(g => grpSet.add(g)));
          setGroups([...grpSet].sort((a,b) => a.localeCompare(b, undefined, { numeric: true })).map(g => ({ groupName: g, displayName: g, things: [] })));
        }
      } catch (e) {
        console.error('初期データ取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  }, [currentRange, fetchFlights]);

  const droneMap = Object.fromEntries(drones.map(d => [d.thingName, d]));

  const bookedThings = new Set(
    flights
      .filter(f => ['planned', 'in_flight'].includes(f.status))
      .map(f => f.thingName)
  );

  const filteredFlights = flights.filter(f => {
    // 表示チェックされている機体のみ
    if (!visibleThings.has(f.thingName)) return false;
    if (filterThing && f.thingName !== filterThing) return false;
    if (filterGroup && !filterGroupObj?.things?.includes(f.thingName)) return false;
    return true;
  });

  const calendarEvents = filteredFlights.map((f) => {
    // 色の優先順位: キャンセル→完了→カテゴリ→デフォルト
    let color;
    if (f.status === 'cancelled') {
      color = STATUS_COLORS.cancelled;
    } else if (f.status === 'completed') {
      color = STATUS_COLORS.completed;
    } else {
      color = FLIGHT_CATEGORIES[f.category]?.color ?? FLIGHT_CATEGORIES.flight.color;
    }
    return {
      id:              f.flightId,
      title:           f.displayName ?? f.thingName,
      start:           f.startTime,
      end:             f.endTime,
      backgroundColor: color,
      borderColor:     color,
      extendedProps:   f,
    };
  });

  // カレンダーイベントのカスタム表示
  const renderEventContent = (eventInfo) => {
    const f = eventInfo.event.extendedProps;
    const categoryLabel = FLIGHT_CATEGORIES[f.category]?.label ?? '';

    return (
      <div className="fc-event-custom text-white text-xs leading-tight p-1" style={{ fontSize: '11px' }}>
        <div className="font-semibold">{f.displayName ?? f.thingName}</div>
        <div>{categoryLabel} / {f.pilotName || '未設定'}</div>
        {f.location && <div className="opacity-80">{f.location}</div>}
      </div>
    );
  };

  const handleDatesSet = (info) => {
    const from = info.startStr.split('T')[0];
    const to   = info.endStr.split('T')[0];
    setCurrentRange({ from, to });
    fetchFlights(from, to);
  };

  const handleDateSelect = (info) => {
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const start = info.allDay ? `${info.startStr}T09:00` : toLocal(info.start);
    const end   = info.allDay ? `${info.startStr}T10:00` : toLocal(info.end);
    setForm({ ...EMPTY_FORM, startTime: start, endTime: end, groupName: filterGroup, thingName: filterThing });
    setSelectedFlight(null);
    setFormError('');
    setShowModal(true);
  };

  const handleEventClick = (info) => {
    setSelectedFlight(info.event.extendedProps);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const handleMiniCalendarDateClick = (date) => {
    setSelectedDate(date);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.gotoDate(date);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      if (name === 'groupName') next.thingName = '';
      return next;
    });
  };

  const selectedGroupObj = groups.find(g => g.groupName === form.groupName);
  const formDrones       = form.groupName
    ? drones.filter(d => (selectedGroupObj?.things ?? d.groups ?? []).includes(d.thingName)
        || (d.groups ?? []).includes(form.groupName))
    : drones;
  const availableDrones = formDrones.filter(d => d.operationalStatus === 'available');

  const handleSubmit = async () => {
    setFormError('');
    if (!form.pilotId)   return setFormError('パイロットを選択してください');
    if (!form.startTime) return setFormError('開始時刻を入力してください');
    if (!form.endTime)   return setFormError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setFormError('終了時刻は開始時刻より後にしてください');

    const isGroupBulk = form.groupName && !form.thingName;
    if (isGroupBulk) {
      const targets = formDrones.filter(d => d.operationalStatus === 'available');
      if (targets.length === 0) return setFormError('グループ内に待機中の機体がありません');
      if (!window.confirm(`グループ「${form.groupName}」の待機中機体 ${targets.length} 台に一括予約します。よろしいですか？`)) return;
    } else if (!form.thingName) {
      return setFormError('機体を選択するか、グループの未選択で一括予約してください');
    }

    setSubmitting(true);
    try {
      if (isGroupBulk) {
        const targets = formDrones.filter(d => d.operationalStatus === 'available');
        const results = await Promise.allSettled(
          targets.map(d => flightApi.create({
            thingName: d.thingName,
            pilotId:   form.pilotId,
            pilotName: form.pilotName,
            title:     form.title,
            startTime: form.startTime,
            endTime:   form.endTime,
            location:  form.location,
            purpose:   form.purpose,
            notes:     form.notes,
            groupName: form.groupName,
            altitude:  form.altitude ? Number(form.altitude) : 0,
            category:  form.category,
          }))
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          setFormError(`${targets.length - failed.length}件成功、${failed.length}件失敗（時間帯の重複等）`);
        } else {
          setShowModal(false);
        }
      } else {
        await flightApi.create({
          thingName: form.thingName,
          pilotId:   form.pilotId,
          pilotName: form.pilotName,
          title:     form.title,
          startTime: form.startTime,
          endTime:   form.endTime,
          location:  form.location,
          purpose:   form.purpose,
          notes:     form.notes,
          groupName: form.groupName,
          altitude:  form.altitude ? Number(form.altitude) : 0,
          category:  form.category,
        });
        setShowModal(false);
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      setFormError(
        e.message.includes('重複') || e.message.includes('CONFLICT') || e.message.includes('予約')
          ? 'この時間帯はすでに予約されています'
          : e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約をキャンセルしますか？')) return;
      try {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirm(flight.groupName, 'キャンセル');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime === flight.startTime
        );
        await Promise.allSettled(
          targets.map(f => flightApi.update(f.flightId, { status: 'cancelled' }))
        );
      } else {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約を削除しますか？')) return;
      try {
        await flightApi.delete(flight.flightId);
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirm(flight.groupName, '削除');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime === flight.startTime
        );
        await Promise.allSettled(
          targets.map(f => flightApi.delete(f.flightId))
        );
      } else {
        await flightApi.delete(flight.flightId);
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const showGroupConfirm = (groupName, action) => {
    return new Promise((resolve) => {
      setGroupConfirm({ groupName, action, resolve });
    });
  };

  const handleEventDrop = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const newStart = toLocal(info.event.start);
    const oldStart = flight.startTime.slice(0, 16);
    const diffMs   = new Date(newStart) - new Date(oldStart);

    const oldDurationMs = new Date(flight.endTime) - new Date(flight.startTime);
    const newEnd = info.event.end
      ? toLocal(info.event.end)
      : toLocal(new Date(new Date(newStart).getTime() + oldDurationMs));

    try {
      if (flight.groupName) {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName &&
          f.startTime.slice(0, 16) === oldStart
        );
        if (targets.length === 0) {
          await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
        } else {
          await Promise.allSettled(
            targets.map(f => {
              const s = toLocal(new Date(new Date(f.startTime).getTime() + diffMs));
              const dur = new Date(f.endTime) - new Date(f.startTime);
              const e = toLocal(new Date(new Date(s).getTime() + dur));
              return flightApi.update(f.flightId, { startTime: s, endTime: e });
            })
          );
        }
      } else {
        await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleEventResize = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const newEnd = toLocal(info.event.end);
    try {
      if (flight.groupName) {
        const oldEnd = flight.endTime.slice(0, 16);
        const diffMs = new Date(newEnd) - new Date(oldEnd);
        const targets = flights.filter(f =>
          f.groupName === flight.groupName &&
          f.startTime.slice(0, 16) === flight.startTime.slice(0, 16)
        );
        await Promise.allSettled(
          targets.map(f => {
            const e = toLocal(new Date(new Date(f.endTime).getTime() + diffMs));
            return flightApi.update(f.flightId, { endTime: e });
          })
        );
      } else {
        await flightApi.update(flight.flightId, { endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleUpdate = async (flight, updatedForm, choiceOverride) => {
    const payload = {
      pilotId:   updatedForm.pilotId,
      pilotName: updatedForm.pilotName,
      title:     updatedForm.title,
      startTime: updatedForm.startTime,
      endTime:   updatedForm.endTime,
      location:  updatedForm.location,
      purpose:   updatedForm.purpose,
      notes:     updatedForm.notes,
      altitude:  updatedForm.altitude ? Number(updatedForm.altitude) : 0,
      category:  updatedForm.category,
    };

    let choice = choiceOverride;
    if (flight.groupName && choice === null) {
      choice = await new Promise(resolve => {
        setTimeout(() => {
          setGroupConfirm({ groupName: flight.groupName, action: '変更', resolve });
        }, 0);
      });
      if (!choice) return;
    }

    if (choice === 'single') {
      payload.groupName = '';
      await flightApi.update(flight.flightId, payload);
    } else if (choice === 'group') {
      const targets = flights.filter(f =>
        f.groupName === flight.groupName && f.startTime === flight.startTime
      );
      await Promise.allSettled(
        targets.map(f => flightApi.update(f.flightId, payload))
      );
    } else {
      await flightApi.update(flight.flightId, payload);
    }
    setShowModal(false);
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  };

  const toggleThingVisibility = (thingName) => {
    setVisibleThings(prev => {
      const next = new Set(prev);
      if (next.has(thingName)) {
        next.delete(thingName);
      } else {
        next.add(thingName);
      }
      return next;
    });
  };

  const toggleAllThings = () => {
    if (visibleThings.size === drones.length) {
      setVisibleThings(new Set());
    } else {
      setVisibleThings(new Set(drones.map(d => d.thingName)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-nexus-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col -m-6 h-[calc(100vh-88px)]">
      {/* メインコンテンツ（2カラム） */}
      <div className="flex flex-1 overflow-hidden">
      {/* 左サイドバー - Googleカレンダー風 */}
      <div
        className="nexus-row-slide-in w-56 flex-shrink-0 bg-white overflow-y-auto flex flex-col"
        style={{ animationDelay: '0ms' }}
      >
        {/* 上部: ミニカレンダー（線なし） */}
        <div className="flex-shrink-0">
          {/* ミニカレンダー */}
          <div className="p-3">
            <MiniCalendar
              selectedDate={selectedDate}
              onDateClick={handleMiniCalendarDateClick}
            />
          </div>
        </div>

        {/* 下部: 機体フィルター（右側に線あり） */}
        <div className="flex-1 border-r border-border-light">
        {/* 機体・グループリスト（順序変更可能） */}
        {sectionOrder.map(sectionId => {
          if (sectionId === 'drones') {
            return (
              <div
                key="drones"
                className="py-2 border-t border-border-light cursor-move"
                draggable
                onDragStart={(e) => handleSectionDragStart(e, 'drones')}
                onDragEnd={handleSectionDragEnd}
                onDragOver={handleSectionDragOver}
                onDrop={(e) => handleSectionDrop(e, 'drones')}
              >
                <button
                  onClick={() => setDronesExpanded(!dronesExpanded)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-light-neutral"
                >
                  <Icon name="gripVertical" className="w-3 h-3 text-outline-variant" />
                  <Icon
                    name={dronesExpanded ? "chevronDown" : "chevronRight"}
                    className="w-4 h-4 text-outline"
                  />
                  <span className="text-sm font-medium text-text-primary">機体</span>
                  <span className="text-xs text-outline ml-auto">{drones.length}</span>
                </button>

                {dronesExpanded && (
                  <div className="mt-1">
                    {/* 全選択/解除 */}
                    <button
                      onClick={toggleAllThings}
                      className="w-full flex items-center gap-2 px-3 py-1 text-left hover:bg-light-neutral"
                    >
                      <span className={`w-3 h-3 rounded-sm border flex items-center justify-center ${
                        visibleThings.size === drones.length ? 'bg-nexus-primary border-nexus-primary' : 'border-border-light'
                      }`}>
                        {visibleThings.size === drones.length && (
                          <Icon name="check" className="w-2 h-2 text-white" />
                        )}
                      </span>
                      <span className="text-xs text-text-secondary">すべて選択</span>
                    </button>

                    {/* 機体リスト（最大12件表示、スクロール） */}
                    <div className="max-h-72 overflow-y-auto">
                      {filteredDrones.map(d => (
                        <button
                          key={d.thingName}
                          onClick={() => toggleThingVisibility(d.thingName)}
                          className="w-full flex items-center gap-2 px-3 py-1 text-left hover:bg-light-neutral group"
                        >
                          <span
                            className={`w-3 h-3 rounded-sm border flex items-center justify-center transition-colors`}
                            style={{
                              backgroundColor: visibleThings.has(d.thingName) ? (d.color || OPERATIONAL_COLORS[d.operationalStatus] || '#0068B7') : 'transparent',
                              borderColor: d.color || OPERATIONAL_COLORS[d.operationalStatus] || '#0068B7'
                            }}
                          >
                            {visibleThings.has(d.thingName) && (
                              <Icon name="check" className="w-2 h-2 text-white" />
                            )}
                          </span>
                          <span className="text-sm text-text-primary truncate flex-1">{d.displayName}</span>
                          {bookedThings.has(d.thingName) && (
                            <span className="w-1.5 h-1.5 rounded-full bg-nexus-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          if (sectionId === 'groups' && groups.length > 0) {
            return (
              <div
                key="groups"
                className="py-2 border-t border-border-light cursor-move"
                draggable
                onDragStart={(e) => handleSectionDragStart(e, 'groups')}
                onDragEnd={handleSectionDragEnd}
                onDragOver={handleSectionDragOver}
                onDrop={(e) => handleSectionDrop(e, 'groups')}
              >
                <button
                  onClick={() => setGroupsExpanded(!groupsExpanded)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-light-neutral"
                >
                  <Icon name="gripVertical" className="w-3 h-3 text-outline-variant" />
                  <Icon
                    name={groupsExpanded ? "chevronDown" : "chevronRight"}
                    className="w-4 h-4 text-outline"
                  />
                  <span className="text-sm font-medium text-text-primary">グループ</span>
                  <span className="text-xs text-outline ml-auto">{groups.length}</span>
                </button>

                {groupsExpanded && (
                  <div className="mt-1 max-h-72 overflow-y-auto">
                    {groups.map(g => (
                      <button
                        key={g.groupName}
                        onClick={() => {
                          if (filterGroup === g.groupName) {
                            setFilterGroup('');
                          } else {
                            setFilterGroup(g.groupName);
                          }
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-1 text-left hover:bg-light-neutral ${
                          filterGroup === g.groupName ? 'bg-nexus-blue-soft' : ''
                        }`}
                      >
                        <Icon name="folder" className="w-3.5 h-3.5 text-outline" />
                        <span className="text-sm text-text-primary truncate flex-1">{g.displayName}</span>
                        <span className="text-xs text-outline">{(g.things ?? []).length}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return null;
        })}

        {/* アラート（固定位置） */}
        {alerts.length > 0 && (
          <div className="py-2 border-t border-border-light">
            <button
              onClick={() => setAlertsExpanded(!alertsExpanded)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-light-neutral"
            >
              <Icon
                name={alertsExpanded ? "chevronDown" : "chevronRight"}
                className="w-4 h-4 text-outline"
              />
              <Icon name="alertTriangle" className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-sm font-medium text-text-primary">点検予定</span>
              <Badge size="sm" variant="warning" className="ml-auto">{alerts.length}</Badge>
            </button>

            {alertsExpanded && (
              <div className="mt-1 px-3 space-y-1 max-h-48 overflow-y-auto">
                {alerts.map(a => (
                  <div key={a.maintId} className="text-xs py-1">
                    <div className="font-medium text-text-primary truncate">{a.displayName ?? a.thingName}</div>
                    <div className="text-outline">{a.scheduledDate}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* メインカレンダー */}
      <div
        className="nexus-row-slide-in flex-1 bg-white flex flex-col overflow-hidden"
        style={{ animationDelay: '100ms' }}
      >
        <div className="flex-1 p-4 pb-0 overflow-hidden">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView="timeGridWeek"
            locale={jaLocale}
            headerToolbar={{
              left:   'prev,next today',
              center: 'title',
              right:  'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
            }}
            buttonText={{ today: '今日', month: '月', week: '週', day: '日', list: 'リスト' }}
            height="100%"
            slotMinTime="06:00:00"
            slotMaxTime="21:00:00"
            slotDuration="00:30:00"
            events={calendarEvents}
            selectable={true}
            selectMirror={true}
            unselectAuto={false}
            select={handleDateSelect}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            editable={true}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventDisplay="block"
            eventContent={renderEventContent}
            nowIndicator={true}
          />
        </div>

        {/* 凡例 */}
        <div className="flex flex-wrap gap-4 px-4 py-2 border-t border-border-light bg-light-neutral">
          {Object.entries(FLIGHT_CATEGORIES).map(([key, { label, color }]) => (
            <div key={key} className="flex items-center gap-1.5 text-sm text-text-secondary">
              <span
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: color }}
              />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* グループ確認モーダル */}
      <Modal
        isOpen={!!groupConfirm}
        onClose={() => { setGroupConfirm(null); groupConfirm?.resolve(null); }}
        title="対象を選択"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setGroupConfirm(null); groupConfirm?.resolve(null); }}>
              閉じる
            </Button>
            <Button variant="secondary" onClick={() => { setGroupConfirm(null); groupConfirm?.resolve('single'); }}>
              この機体のみ
            </Button>
            <Button variant="danger" onClick={() => { setGroupConfirm(null); groupConfirm?.resolve('group'); }}>
              グループ全体
            </Button>
          </>
        }
      >
        {groupConfirm && (
          <div className="space-y-2">
            <p className="text-text-primary">「{groupConfirm.groupName}」グループで登録された予約です。</p>
            <p className="text-text-primary">{groupConfirm.action}する対象を選択してください。</p>
          </div>
        )}
      </Modal>

      {/* 予約ドロワー */}
      <FlightDrawer
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          // ドラッグ選択範囲を解除 (新規予約の未確定キャンセル時など)
          calendarRef.current?.getApi()?.unselect();
        }}
        selectedFlight={selectedFlight}
        droneMap={droneMap}
        users={users}
        form={form}
        groups={groups}
        availableDrones={availableDrones}
        allDrones={formDrones}
        formError={formError}
        submitting={submitting}
        onFormChange={handleFormChange}
        onFormSubmit={handleSubmit}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
      />
      </div>
    </div>
  );
}

const FLIGHT_TABS = [
  { key: 'basic', label: '基本情報' },
  { key: 'details', label: '詳細オプション' },
];

function FlightDrawer({
  isOpen, onClose, selectedFlight, droneMap, users,
  form, groups, availableDrones, allDrones, formError, submitting,
  onFormChange, onFormSubmit, onCancel, onDelete, onUpdate,
}) {
  const [currentTab, setCurrentTab] = React.useState('basic');
  React.useEffect(() => {
    if (isOpen) setCurrentTab('basic');
  }, [isOpen]);

  const title = selectedFlight ? (
    <span className="flex items-center gap-2">
      <span
        className="w-3 h-3 rounded-full"
        style={{ background: selectedFlight.droneColor ?? droneMap[selectedFlight.thingName]?.color ?? '#0068B7' }}
      />
      <span>{selectedFlight.displayName ?? selectedFlight.thingName}</span>
      {selectedFlight.groupName && (
        <Badge size="sm">{selectedFlight.groupName}</Badge>
      )}
    </span>
  ) : '新規予約';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title} width="640px">
      <Tabs value={currentTab} onChange={setCurrentTab} tabs={FLIGHT_TABS} />
      <div className="p-6">
        {selectedFlight ? (
          <FlightDetail
            flight={selectedFlight}
            users={users}
            currentTab={currentTab}
            onCancel={onCancel}
            onDelete={onDelete}
            onUpdate={onUpdate}
            onClose={onClose}
          />
        ) : (
          <FlightForm
            form={form}
            groups={groups}
            availableDrones={availableDrones}
            allDrones={allDrones}
            users={users}
            error={formError}
            submitting={submitting}
            currentTab={currentTab}
            onChange={onFormChange}
            onSubmit={onFormSubmit}
            onClose={onClose}
          />
        )}
      </div>
    </Drawer>
  );
}

function FlightDetail({ flight, users, currentTab = 'basic', onCancel, onDelete, onUpdate, onClose }) {
  const [form, setForm] = React.useState({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });
  const [error,      setError]      = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.pilotId)   return setError('パイロットを選択してください');
    if (!form.startTime) return setError('開始時刻を入力してください');
    if (!form.endTime)   return setError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setError('終了時刻は開始時刻より後にしてください');
    setSubmitting(true);
    try {
      await onUpdate(flight, form, flight.groupName ? null : 'single');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {currentTab === 'basic' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Input
              label="タイトル（任意）"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="例: 現場点検フライト"
            />
          </div>

          <Select
            label="パイロット"
            name="pilotId"
            value={form.pilotId}
            onChange={handleChange}
            options={[
              { value: '', label: '選択してください' },
              ...users.map(u => ({ value: u.userId, label: u.name ?? u.email }))
            ]}
          />

          <div className="leading-normal">
            <label className="block text-sm font-medium text-text-primary mb-1">ステータス</label>
            <Badge variant={flight.status === 'planned' ? 'primary' : flight.status === 'in_flight' ? 'success' : flight.status === 'cancelled' ? 'danger' : 'default'}>
              {STATUS_LABELS[flight.status] ?? flight.status}
            </Badge>
          </div>

          <Select
            label="予約種別"
            name="category"
            value={form.category}
            onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
            options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({
              value: key,
              label: label
            }))}
          />

          <Input
            label="開始日時"
            type="datetime-local"
            name="startTime"
            value={form.startTime}
            onChange={handleChange}
          />

          <Input
            label="終了日時"
            type="datetime-local"
            name="endTime"
            value={form.endTime}
            onChange={handleChange}
          />
        </div>
      )}

      {currentTab === 'details' && (
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="場所"
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder="例: 東京都港区海岸"
          />

          <Input
            label="最大高度 (m)"
            type="number"
            name="altitude"
            value={form.altitude}
            onChange={handleChange}
            placeholder="例: 150"
          />

          <div className="col-span-2">
            <Input
              label="目的"
              name="purpose"
              value={form.purpose}
              onChange={handleChange}
              placeholder="例: 測量・点検・農薬散布"
            />
          </div>

          <div className="col-span-2">
            <Textarea
              label="備考"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </div>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-border-light">
        <div className="flex gap-2">
          {flight.status === 'planned' && (
            <Button variant="danger-outline" onClick={() => onCancel(flight)} disabled={submitting}>
              キャンセル
            </Button>
          )}
          {['planned', 'cancelled'].includes(flight.status) && (
            <Button variant="danger" onClick={() => onDelete(flight)} disabled={submitting}>
              <Icon name="trash" className="w-4 h-4" />
              削除
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            閉じる
          </Button>
          {isDirty && (
            <Button onClick={handleSave} loading={submitting}>
              変更を保存
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FlightForm({ form, groups, availableDrones, allDrones, users, error, submitting, currentTab = 'basic', onChange, onSubmit, onClose }) {
  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {currentTab === 'basic' && (
      <div className="space-y-4">
        {/* グループ選択 */}
        {groups.length > 0 && (
          <Select
            label="グループ（一括予約用）"
            name="groupName"
            value={form.groupName}
            onChange={onChange}
            options={[
              { value: '', label: '指定なし' },
              ...groups.map(g => ({ value: g.groupName, label: g.displayName }))
            ]}
          />
        )}

        {/* 機体選択 */}
        <Select
          label="機体"
          name="thingName"
          value={form.thingName}
          onChange={onChange}
          options={[
            { value: '', label: '選択してください' },
            ...(availableDrones.length > 0
              ? [
                  { value: '', label: '── 待機中 ──', disabled: true },
                  ...availableDrones.map(d => ({ value: d.thingName, label: d.displayName })),
                  ...(allDrones.filter(d => d.operationalStatus !== 'available').length > 0
                    ? [
                        { value: '', label: '── 点検中・修理中 ──', disabled: true },
                        ...allDrones.filter(d => d.operationalStatus !== 'available').map(d => ({
                          value: d.thingName,
                          label: `${d.displayName}（${d.operationalStatus === 'maintenance' ? '点検中' : '修理中'}）`,
                          disabled: true
                        }))
                      ]
                    : [])
                ]
              : allDrones.map(d => ({ value: d.thingName, label: d.displayName })))
          ]}
        />

        {/* 日時 */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="開始日時"
            type="datetime-local"
            name="startTime"
            value={form.startTime}
            onChange={onChange}
          />
          <Input
            label="終了日時"
            type="datetime-local"
            name="endTime"
            value={form.endTime}
            onChange={onChange}
          />
        </div>

        {/* パイロット */}
        <Select
          label="パイロット"
          name="pilotId"
          value={form.pilotId}
          onChange={onChange}
          options={[
            { value: '', label: '選択してください' },
            ...users.map(u => ({ value: u.userId, label: u.name ?? u.email }))
          ]}
        />

        {/* 予約種別 */}
        <Select
          label="予約種別"
          name="category"
          value={form.category}
          onChange={onChange}
          options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({
            value: key,
            label: label
          }))}
        />
      </div>
      )}

      {currentTab === 'details' && (
        <div className="space-y-3">
          <Input
            label="タイトル"
            name="title"
            value={form.title}
            onChange={onChange}
            placeholder="例: 現場点検フライト"
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="場所"
              name="location"
              value={form.location}
              onChange={onChange}
              placeholder="例: 東京都港区海岸"
            />
            <Input
              label="最大高度 (m)"
              type="number"
              name="altitude"
              value={form.altitude}
              onChange={onChange}
              placeholder="例: 150"
            />
          </div>

          <Input
            label="目的"
            name="purpose"
            value={form.purpose}
            onChange={onChange}
            placeholder="例: 測量・点検・農薬散布"
          />

          <Textarea
            label="備考"
            name="notes"
            value={form.notes}
            onChange={onChange}
            rows={3}
            placeholder="特記事項があれば入力"
          />
        </div>
      )}

      {/* ボタン */}
      <div className="flex justify-end gap-3 pt-3 border-t border-border-light">
        <Button variant="secondary" onClick={onClose}>
          キャンセル
        </Button>
        <Button onClick={onSubmit} loading={submitting}>
          予約登録
        </Button>
      </div>
    </div>
  );
}
